import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { FieldGrid, FieldGridItem, RecordSection, StatusChip } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IOversightUsage } from '../api/oversight-contracts';
import type { IRosterCard, IRosterCardSlot } from '../api/participant-contracts';
import { participantsApi } from '../api/participants-api';
import { answerText, NO_ANSWER } from '../answer-text';
import { AdminPage, LoadError, TableSkeleton, useAdminData } from '../components/admin-page';
import { CopyableValue } from '../components/copyable-value';
import {
  CardLink,
  PEOPLE_PATH,
  ParticipantLink,
  searchFrom,
  wayBack
} from '../components/participant-link';
import { responsePath } from '../paths';
import { slotWords } from '../roster-words';

/**
 * An unregistered member's card (SR-414, record search §3.6; the plan's D12) —
 * `/team/people/roster/:rosterKey`, beside People, which lists the member as a roster row while
 * it is searched. `GET /admin/people/roster/{rosterKey}`, `responses.browse`.
 *
 * **What the rosters say, and nothing decided here.** A member we hold no participant for is only
 * what the teams' and firms' responses answered about them: the card draws each slot naming them,
 * newest first as sent — the team or firm (linked under its own list), the survey and its
 * response, the slot's answers as answered, and the member group's questions with that slot's
 * answers alone, each read as the response's Answers tab reads it (`answerText`). No control, no
 * correction: a correction is the response's, on its Answers tab, and who the member is is
 * Veritant's decision on the team's or firm's roster.
 *
 * **A 404 is a page of its own**, never "no such participant": survey-api answers it once no
 * unresolved slot answers to the key — the member registered, or Veritant linked them — so the card
 * says that and points back at the search. Three arms otherwise: skeleton, load error, loaded.
 *
 * **Remounted per key**, the hub's lesson: the route's element is not remounted when the
 * parameter changes, and the list beside it opens one card after another.
 */
export function AdminRosterCardPage() {
  const { rosterKey = '' } = useParams();
  const { state, search } = useLocation();
  const from = searchFrom(state) || search;
  return (
    <RosterCardPage
      key={rosterKey}
      rosterKey={rosterKey}
      back={wayBack(state, PEOPLE_PATH, 'People', from)}
    />
  );
}

function RosterCardPage({
  rosterKey,
  back
}: {
  rosterKey: string;
  back: { to: string; words: string };
}) {
  // Wrapped, the hub's reason: a bare `null` for a 404 would read as "not loaded yet".
  const { data, error } = useAdminData<{ card: Nullable<IRosterCard> }>(
    async (signal) => ({ card: await participantsApi.getRosterCard(rosterKey, signal) }),
    [rosterKey]
  );
  const card = data?.card ?? null;

  return (
    <AdminPage
      title={card?.displayName ?? 'Roster member'}
      crumb={
        <Link
          to={back.to}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-sapphire)] underline-offset-4 hover:underline"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {back.words}
        </Link>
      }
    >
      {data === null ? (
        error ? (
          <LoadError message={error} />
        ) : (
          <TableSkeleton rows={4} />
        )
      ) : card === null ? (
        <Alert data-roster-gone>
          <AlertTitle>Nobody unregistered answers to this any more</AlertTitle>
          <AlertDescription>
            They have registered, or Veritant has linked them. Search People again to find their
            record.
          </AlertDescription>
        </Alert>
      ) : (
        <RosterCardBody card={card} />
      )}
    </AdminPage>
  );
}

function RosterCardBody({ card }: { card: IRosterCard }) {
  return (
    <>
      <section aria-label="Who this is" className="grid gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip>Not registered</StatusChip>
          {card.crd !== null && <CopyableValue label="CRD" value={card.crd} name="Copy the CRD" />}
        </div>
        {card.emails.length > 0 && (
          <ul aria-label="Emails" className="grid gap-1">
            {card.emails.map((email) => (
              <li key={email}>
                <CopyableValue value={email} name={`Copy ${email}`} />
              </li>
            ))}
          </ul>
        )}
        <p className="text-sm text-muted-foreground">
          Named on a team’s or firm’s roster, with no account and no record of their own. Once they
          register or Veritant links them, this page gives way to their record.
        </p>
      </section>

      {card.slots.map((slot) => (
        <SlotSection key={`${slot.responseId} ${slot.slot}`} slot={slot} />
      ))}
    </>
  );
}

/** The roster's name on a team's and a firm's page — the section where Veritant decides a slot. */
function rosterLabel(recordType: string): string {
  if (recordType === 'team') return 'Advisors';
  if (recordType === 'firm') return 'Principals';
  return 'Advisors & principals';
}

/** One slot naming the member: the record it is on, its response, what the slot answered and the
 *  member group's questions. */
function SlotSection({ slot }: { slot: IRosterCardSlot }) {
  const facts: Array<{ label: string; value: Nullable<string> }> = [
    { label: 'Full name', value: slot.fullName },
    { label: 'CRD', value: slot.crd },
    { label: 'Email', value: slot.email }
  ];
  const answered = slot.answers.filter((usage) => usage.answers.length > 0).length;
  const named = facts.filter((fact) => fact.value !== null).length;
  return (
    <RecordSection
      page="roster-card"
      sectionKey="slot"
      title={`${slot.displayName} — ${slotWords(slot)}`}
      level={2}
      defaultOpen
      filled={named + answered}
      total={facts.length + slot.answers.length}
    >
      <div className="grid gap-4">
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
          <dt className="text-muted-foreground">On</dt>
          <dd>
            <ParticipantLink participant={slot} />
          </dd>
          <dt className="text-muted-foreground">Survey</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <StatusChip>{slot.surveyCode}</StatusChip>
            <CardLink to={responsePath(slot.responseId)}>Open the response</CardLink>
          </dd>
          <dt className="text-muted-foreground">As of</dt>
          <dd>{formatDateTime(slot.asOf)}</dd>
        </dl>

        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">As answered</h3>
          <FieldGrid>
            {facts.map((fact) => (
              <FieldGridItem key={fact.label} label={fact.label} filled={fact.value !== null}>
                {fact.value ?? NO_ANSWER}
              </FieldGridItem>
            ))}
          </FieldGrid>
        </div>

        {slot.answers.length > 0 && (
          <div className="grid gap-2">
            <h3 className="text-sm font-semibold">The slot’s answers</h3>
            <FieldGrid>
              {slot.answers.map((usage) => (
                <FieldGridItem
                  key={usage.code}
                  label={usage.label}
                  filled={usage.answers.length > 0}
                >
                  {usageText(usage)}
                </FieldGridItem>
              ))}
            </FieldGrid>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          To say who this is, decide it under {rosterLabel(slot.recordType)} on{' '}
          <ParticipantLink participant={slot} />’s page.
        </p>
      </div>
    </RecordSection>
  );
}

/** A question's answers for the slot, as the Answers tab reads each — several where the question
 *  holds rows of its own. */
function usageText(usage: IOversightUsage): string {
  return usage.answers.length === 0 ? NO_ANSWER : usage.answers.map(answerText).join('; ');
}
